import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'

import { sum, getUrlParam } from '@/utils'
import { useProductsStore, useReferralStore, useBundleProductsStore, useCartStore } from './'
import { useSpecialDiscountStore } from '@/stores'
import { REFERRAL_DISCOUNT_CENTS } from '@/services/constants'

import { variantSelectUrlParams } from '@/services/bundle-url-params'

import type { BundlePlan, BundleSelectionMap, BundleProductSelection, BundleSize, BundleType, BundlePricing } from '@/types/bundle'
import type { VarietyPackEntry } from '@/types/shopify'

export const useBundleStore = defineStore('bundle', () => {
  const bundleType: BundleType = 'oatmeal'
  const allBundleSizes: BundleSize[] = [ 16, 24, 32 ]

  const referralStore = useReferralStore()
  const { hasReferral } = storeToRefs(referralStore)

  const discountStore = useSpecialDiscountStore()
  const { activeDiscount } = storeToRefs(discountStore)

  const bundleSizes = computed(() => {
    const sizes = activeDiscount.value?.allowedSizes
    if (sizes && bundleType === 'oatmeal') {
      return allBundleSizes.filter(size => sizes.includes(size))
    }
    return allBundleSizes
  })

  const MIN_SIZE = computed(() => bundleSizes.value[0])
  const MAX_SIZE = computed(() => bundleSizes.value[bundleSizes.value.length - 1])

  const productsStore = useProductsStore()
  const bundleProductsStore = useBundleProductsStore()
  const cartStore = useCartStore()

  const { productList } = storeToRefs(productsStore)
  const { onetime, subscription } = storeToRefs(bundleProductsStore)

  const loading = ref(false)
  // const onetimeOnly = ref(window.location.href.includes('instant-protein-coffee'))
  const plan = ref<BundlePlan>('subscription')
  const onetimeConfirm = ref(false)
  const checkoutSwitchToSubscription = ref(false)
  const selectedSize = ref<BundleSize>(allBundleSizes[0])
  const selection = ref<BundleSelectionMap>({})

  //========================================================================================
  // Computed

  const ready = computed(() => !!onetime.value && !!subscription.value && productList.value.length > 0)

  const isSub = computed(() => plan.value === 'subscription')
  const isCheckoutSub = computed(() => isSub.value || checkoutSwitchToSubscription.value)

  const totalQuantity = computed(() => sum(...Object.values(selection.value)))

  const quantityBelowMinimum = computed(() => totalQuantity.value < MIN_SIZE.value)
  const quantityAtMaximum = computed(() => totalQuantity.value === MAX_SIZE.value)

  const size = computed(() => bundleSizes.value.find(i => totalQuantity.value <= i) || MIN_SIZE.value)

  const validSize = computed(() => {
    const quantity = totalQuantity.value
    return quantity === size.value && quantity >= MIN_SIZE.value
  })

  const product = computed(() => {
    return (isCheckoutSub.value ? subscription : onetime).value
  })

  const findVariant = (size: BundleSize) => {
    return product.value?.variants?.find(i => i.size === size) || product.value?.variants?.[0]
  }

  const variant = computed(() => findVariant(size.value))
  const selectedVariant = computed(() => findVariant(selectedSize.value))


  const bundlePricing = computed<Record<number, BundlePricing>>(() => {
    const product = subscription.value
    if (!product) {
      return {}
    }

    let lastSize, lastPrice, upgradeQuantity, upgradeSavings
    return Object.fromEntries(product.variants.map(variant => {
      const specialDiscount = activeDiscount.value?.pricing?.[variant.size]
      const { priceCents: recurringCents, firstOrderCents = recurringCents, onetimePriceCents, firstOrderDiscountPercent, referralDiscountCents } = { ...variant, ...specialDiscount }
      const onetimeCents = onetimePriceCents ?? recurringCents
      const adjustedFirstOrderCents = !specialDiscount && hasReferral.value ? firstOrderCents - (referralDiscountCents ?? REFERRAL_DISCOUNT_CENTS) : firstOrderCents
      const onetimeReferral = onetime.value?.variants.find(v => v.size === variant.size)?.referralDiscountCents ?? REFERRAL_DISCOUNT_CENTS
      const onetimeReferralCents = !specialDiscount && hasReferral.value ? onetimeCents - onetimeReferral : onetimeCents

      const pricePerCents = adjustedFirstOrderCents / variant.size
      if (lastPrice) {
        const savingsPerSize = pricePerCents - lastPrice
        upgradeQuantity = lastSize - variant.size
        upgradeSavings = Math.round(savingsPerSize * lastSize)
      }
      lastSize = variant.size
      lastPrice = pricePerCents

      return [ variant.size, {
        recurringCents,
        firstOrderCents: adjustedFirstOrderCents,
        onetimeCents,
        onetimeReferralCents,
        savings: onetimeCents - recurringCents,
        firstSavings: onetimeCents - adjustedFirstOrderCents,
        upgradeQuantity,
        upgradeSavings,
        firstOrderDiscountPercent
      }]
    }))
  })

  const selectedPricing = computed(() => bundlePricing.value[selectedSize.value] ?? {})
  const pricing = computed(() => bundlePricing.value[size.value] ?? {})
  const priceCents = computed(() => pricing.value[isCheckoutSub.value ? 'firstOrderCents' : 'onetimeReferralCents'])

  // const priceCentsForSize = computed(() => (size: BundleSize) => {
  //   return product.value?.variants?.find(i => i.size === size)?.priceCents
  // })

  const selectedProducts = computed(() => {
    if (!productList.value?.length) {
      return []
    }
    try {
      return Object.entries(selection.value).reverse().map(([ key, quantity ]) => {
        const product = productList.value.find(i => i.key === key)
        if (!product) {
          // delete productList.value[key]
          throw new Error(`Product not found for: ${key}`)
        }
        return {
          key,
          product,
          quantity
        } as BundleProductSelection
      })
    } catch {
      selection.value = {}
      return []
    }
  })

  const bundleItemsAnalyticsData = computed(() => {
    return selectedProducts.value.map((item) => ({
      sku: item.product?.sku,
      quantity: item.quantity
    }))
  })


  const updateLocked = ref(false)

  const setUpdateLocked = (value: boolean) => {
    updateLocked.value = value
  }

  const updateQuantity = (key: string, quantity: number) => {
    if (updateLocked.value) return
    if (quantity === 0) {
      delete selection.value[key]
    } else {
      selection.value[key] = quantity
    }
  }

  const setSelectionFromContents = (contents: VarietyPackEntry[]) => {
    selection.value = Object.fromEntries(contents.map(i => [ i.product?.key, i.quantity ]).reverse())
  }

  // const incrementQuantityUntilLimit = () => {
  //   updateQuantityUntilValue(MIN_SIZE)
  // }

  // const updateQuantityUntilValue = (value: number) => {
  //   while (totalQuantity.value < value) {
  //     for (const [ key, quantity ] of Object.entries(selection.value)) {
  //       updateQuantity(key, quantity + 1)
  //       if (totalQuantity.value === value) {
  //         break
  //       }
  //     }
  //   }

  //   while (totalQuantity.value > value) {
  //     for (const [ key, quantity ] of Object.entries(selection.value)) {
  //       updateQuantity(key, quantity - 1)
  //       if (totalQuantity.value === value) {
  //         break
  //       }
  //     }
  //   }
  // }

  const readUrlParams = () => {
    const items = getUrlParam('items')
    if (items) {
      setEncodedItems(items)
    } else {
      const { type, size } = variantSelectUrlParams()
      if (type) {
        plan.value = type
      }
      setSelectedSize(size)
    }
  }

  const setSelectedSize = (size?: BundleSize) => {
    if (size && totalQuantity.value <= size) {
      selectedSize.value = size
    }
  }

  const recoverV1Selections = () => {
    if (totalQuantity.value) {
      return
    }
    try {
      const json = window.localStorage.getItem('vuex')
      const data = typeof json === 'string' && JSON.parse(json)
      if (data?.bundler?.items?.length) {
        selection.value = Object.fromEntries(data?.bundler?.items.map(i => {
          try {
            const { quantity, product: { nid, variants } } = i
            const key = `${nid}/${variants[0].nid}`
            return [ key, quantity ]
          } catch {
            // nothing
          }
        }).filter(Boolean))
        window.localStorage.removeItem('vuex')
      }
    } catch (e) {
      // nothing
      console.warn(e)
    }
  }

  const validateSelections = () => {
    if (!productList.value.length) {
      return
    }
    for (const key of Object.keys(selection.value)) {
      const product = productList.value.find(i => i.key === key)
      if (!product) {
        // console.log('> not found', key)
        delete selection.value[key]
      }
    }
  }

  const getEncodedItems = () => {
    const map = Object.entries(selection.value).reduce((acc, [ key, quantity ]) => {
      const id = key.split('/')[0]
      acc[quantity] ||= []
      acc[quantity].push(id)
      return acc
    }, {})
    return window.btoa(new URLSearchParams(map).toString())
  }

  const setEncodedItems = (hash: string) => {
    const map = new URLSearchParams(window.atob(hash))
    map.forEach((idString, quantity) => {
      const ids = idString.split(',')
      ids.forEach(id => {
        const product = productList.value.find(i => i.key.startsWith(id))
        if (product) {
          selection.value[product.key] = Number(quantity)
        }
      })
    })
  }

  const setup = async () => {
    if (ready.value || loading.value) {
      return
    }
    loading.value = true
    await bundleProductsStore.load()
    recoverV1Selections()
    validateSelections()
    loading.value = false
  }

  const reset = () => {
    selection.value = {}
    plan.value = 'subscription'
  }

  watch(totalQuantity, (total: number) => {
    const { value } = selectedSize
    if (total <= value || total >= value) {
      selectedSize.value = size.value
    }
    if (total === 0) {
      cartStore.removeBundle()
    }
  })

  // watch(activeDiscount, (discount) => {
  //   if (discount && bundleType === 'oatmeal' && selectedSize.value === 16) {
  //     selectedSize.value = discount.allowedSizes[0]
  //   }
  // })

  setup()

  return {
    ready,
    // onetimeOnly,
    isSub,
    onetimeConfirm,
    selection,
    totalQuantity,
    quantityBelowMinimum,
    quantityAtMaximum,
    validSize,
    size,
    selectedSize,
    selectedPricing,
    plan,
    bundleType,
    bundleSizes,
    bundlePricing,
    pricing,
    priceCents,
    product,
    variant,
    selectedVariant,
    selectedProducts,
    bundleItemsAnalyticsData,
    checkoutSwitchToSubscription,
    MIN_SIZE,
    MAX_SIZE,
    // priceCentsForSize,
    updateLocked,
    setUpdateLocked,
    updateQuantity,
    setSelectionFromContents,
    setSelectedSize,
    getEncodedItems,
    setEncodedItems,
    // updateQuantityUntilValue,
    // incrementQuantityUntilLimit,
    readUrlParams,
    setup,
    reset
  }
}, {
  persist: {
    pick: [
      'size',
      'plan',
      'selection'
    ],
    beforeHydrate: ({ store }) => {
      const zustand = getZustandState()
      if (zustand) {
        store.$state.selection = zustand.selection
        store.$state.plan = zustand.plan
        store.setSelectedSize(zustand.selectedSize)
        store.$persist()
      }
    }
  }
})

const getZustandState = () => {
  try {
    const zustand = window.localStorage.getItem('oon_bundler')
    if (zustand) {
      window.localStorage.removeItem('oon_bundler')
      return JSON.parse(zustand)?.state
    }
  } catch {
    // nothing
  }
}
